import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import {
    useLocalization,
    useSupportedLanguagesMap,
} from '@experiences/locales';
import {
    themeMap,
    useApolloTheme,
} from '@experiences/theme';
import { UiSelect } from '@experiences/ui-common';
import {
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import type { ThemeType } from '@uipath/portal-shell-util';
import {
    defaultLanguage,
    defaultTheme,
    setTheme,
} from '@uipath/portal-shell-util';
import clsx from 'clsx';
import type { FC } from 'react';
import React, {
    useCallback,
    useEffect,
    useRef,
} from 'react';
import {
    Controller,
    useForm,
} from 'react-hook-form';
import { useIntl } from 'react-intl';

import useSetUserLanguage from '../../../common/hooks/useSetUserLanguage.default';
import useSetUserTheme from '../../../common/hooks/useSetUserTheme.default';
import { useTelemetryHelper } from '../../../telemetry/TelemetryHelper';

const useStyles = makeStyles(theme =>
    createStyles({
        formContainer: { maxWidth: '482px' },
        heading: {
            color: theme.palette.semantic.colorForeground,
            fontSize: '16px',
            fontWeight: 600,
        },
        mainHeading: { paddingBottom: '20px' },
        radio: {
            marginTop: '14px',
            maxHeight: '20px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        rowGroup: {
            marginLeft: '8px',
            display: 'flex',
            flexDirection: 'column',
        },
        section: { paddingBottom: '20px' },
        subHeading: {
            color: theme.palette.semantic.colorForeground,
            fontSize: '14px',
            fontWeight: 600,
        },
    }),
);

interface IProfilePreferencesData {
    language: string;
    theme: ThemeType;
}

const ProfilePreferencesComponent: FC = () => {
    const EnableUserTheme = useFeatureFlagValue(Features.EnableUserTheme.name);

    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const setUserLanguage = useSetUserLanguage();
    const setUserTheme = useSetUserTheme();
    const selectedLanguage = useLocalization(false);
    const { selectedThemeId } = useApolloTheme();
    const languageOptions = useSupportedLanguagesMap();
    const {
        control, errors, setValue,
    } = useForm<IProfilePreferencesData>({
        mode: 'onChange',
        defaultValues: {
            language: (selectedLanguage ?? defaultLanguage).toLowerCase(),
            theme: selectedThemeId ?? defaultTheme,
        },
    });
    const prevLangRef = useRef<string>();
    const prevThemeRef = useRef<ThemeType>();
    const { logEvent } = useTelemetryHelper();

    const highContrastLightThemeFlag = useFeatureFlagValue(Features.EnableHighContrastLightTheme.name);

    const handleThemeSelect = useCallback((newTheme: ThemeType) => {
        if (!highContrastLightThemeFlag && newTheme === 'light-hc') {
            return;
        }

        logEvent('Theme.ChangeTheme', {
            ProductThemeOld: prevThemeRef.current,
            ProductThemeNew: newTheme,
        });
        if (EnableUserTheme) {
            setUserTheme(newTheme);
        } else {
            setTheme(newTheme);
        }
        prevThemeRef.current = newTheme;
    }, [ EnableUserTheme, highContrastLightThemeFlag, logEvent, setUserTheme ]);

    const handleLanguageSelect = useCallback((newLanguage) => {
        logEvent('Localization.ChangeLanguage', {
            ProductLanguageOld: prevLangRef.current,
            ProductLanguageNew: newLanguage,
        });
        setUserLanguage(newLanguage);
        prevLangRef.current = newLanguage;
    }, [ logEvent, setUserLanguage ]);

    useEffect(() => {
        if (selectedThemeId) {
            setValue('theme', selectedThemeId);
        }
    }, [ selectedThemeId, setValue ]);

    useEffect(() => {
        if (selectedLanguage) {
            setValue('language', selectedLanguage.toLowerCase());
        }
    }, [ selectedLanguage, setValue ]);

    let themes = Object.entries(themeMap);
    if (!highContrastLightThemeFlag) {
        themes = themes.filter(([ theme ]) => theme !== 'light-hc');
    }

    return (
        <div className={classes.formContainer}>
            <div className={classes.section}>
                <Typography
                    className={clsx(classes.heading, classes.mainHeading)}
                    role="heading"
                    aria-level={2}>
                    { translate({ id: highContrastLightThemeFlag ? 'CLIENT_LANGUAGE' : 'CLIENT_PREFERENCES' })}
                </Typography>
                <UiSelect
                    control={control}
                    dataCy="profile-preferences-language-select"
                    inputLabel={translate({ id: 'CLIENT_LANGUAGE' })}
                    name="language"
                    options={languageOptions}
                    error={!!errors.language}
                    fullWidth
                    isTranslated
                    required
                    onChange={event => handleLanguageSelect(event.target.value)}
                />
            </div>
            {!highContrastLightThemeFlag && (
                <div className={classes.section}>
                    <Typography className={classes.subHeading}>
                        {translate({ id: 'CLIENT_THEME' })}
                    </Typography>
                    <Typography>
                        {translate({ id: 'CLIENT_THEME_CHANGE_DESCRIPTION' })}
                    </Typography>
                    <Controller
                        control={control}
                        name="theme"
                        rules={{ required: true }}
                        render={({
                            onChange, value,
                        }) => (
                            <RadioGroup
                                data-cy="profile-preferences-theme-radio-buttons"
                                aria-label={translate({ id: 'CLIENT_THEME_CONTROL_DESCRIPTION' })}
                                className={classes.rowGroup}
                                value={value}
                                onChange={event => {
                                    onChange(event.target.value);
                                    handleThemeSelect(event.target.value as ThemeType);
                                }}
                            >
                                {themes.map(([ reasonId, reasonValue ], i) => (
                                    <FormControlLabel
                                        key={i}
                                        className={classes.radio}
                                        control={<Radio
                                            value={reasonId}
                                            checked={value === reasonId}
                                            color="primary" />}
                                        label={translate({ id: reasonValue })}
                                    />
                                ))}
                            </RadioGroup>
                        )}
                    />
                </div>)}
        </div>
    );
};

export default ProfilePreferencesComponent;
